import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtHelper } from 'angular2-jwt';
import { SubscriptionService } from './services/subscription.service';
import { SharedDataService } from './services/shared-data.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { CONSTANTS } from './shared/constants';
import { arrayInObjectIsEmpty, arrayContainsValue } from './shared/array-functions';
declare var gigya: any;
declare var jQuery: any;

@Injectable()
export class AuthenticationService {
  private authenticated = new BehaviorSubject<boolean>(false);  // Stream of logged in status to communicate throughout the application
  private localStorage = window.localStorage;
  private sessionStorage = window.sessionStorage;
  private jwtHelper: JwtHelper = new JwtHelper();
  userProfile: any;
  token: string;  
  gigyaId: any;
  loginProvider: string;
  typeOfUser: string;

  /*
   * When application loads, retrieve JWT token and check if it is expired
   * If token is not expired, set logged in status to true,  retrieve user's gigya info (Gigya User Id and User Profile) and save session data
   * If token does not exist or if it is expired, set logged in status to false and removes session data (clear data from localStorage)
  */
  constructor(private router: Router, private subscriptionService: SubscriptionService, private sharedDataService: SharedDataService, public googleAnalyticsService: GoogleAnalyticsService) {
    this.getAccountInfo().subscribe(
      (eventObj) => { 
        console.log(eventObj);
        this.userProfile = eventObj.profile;
        this.gigyaId = eventObj.UID;
        this.getJWT().subscribe((jwtToken) => {
          this.token = jwtToken;
          this.localStorage.setItem('session', JSON.stringify({ token: jwtToken, uid: eventObj.UID, provider: eventObj.loginProvider }));
          this.getUserInfo();
        });
    });
    JSON.parse(this.localStorage.getItem('session')) && this.isTokenExpired() ? this.logoutUserExpiredSession() : '';
  }

  /*
   * Update logged in status (if user is authenticated or not)
  */
  setIsAuthenticated(value: boolean): void {
    this.authenticated.next(value);
  }

  /*
   * Retrieve logged in status (if user is authenticated or not)
  */
  getIsAuthenticated = () : Observable<boolean> => {
    return this.authenticated.asObservable();
  }

  /*
   * Show Gigya login screenset and add event handler function
  */
  login(): void {
    gigya.accounts.showScreenSet({
      screenSet: CONSTANTS.gigyaConfig.screenSetId,
      startScreen: CONSTANTS.gigyaConfig.loginStartScreenSet,
      lang: CONSTANTS.gigyaConfig.language,
      regSource: CONSTANTS.gigyaConfig.domain,
      sessionExpiration: -2
    });
    gigya.accounts.addEventHandlers({
      onLogin: this.loginEventHandler
    });  
  }

  /*
   * Gigya login event handler that is executed when user has successfully logged in
   * Stores Gigya User Id and User Profile, creates a JWT token and stores session
   * Calls method that retrieves piano info and subscriptions 
  */
  loginEventHandler = (eventObj) => {
    console.log(eventObj);
    let provider;
    if (eventObj.newUser) {
      this.getAccountInfo().subscribe(
        (accountInfo) => { 
          provider = accountInfo.loginProvider;
      });
    }
    this.userProfile = eventObj.profile;
    this.gigyaId = eventObj.UID;
    this.getJWT().subscribe((jwtToken) => {
      this.token = jwtToken;
      provider ? this.loginProvider = provider : this.loginProvider = eventObj.provider;
      this.localStorage.setItem('session', JSON.stringify({ token: jwtToken, uid: eventObj.UID, provider: this.loginProvider }));
      this.getUserInfo();
    });
  }

  /*
   * Show Gigya register screenset for profile anonymous view and add event handler function
  */
  register(): void {
    gigya.accounts.showScreenSet({
      screenSet: CONSTANTS.gigyaConfig.screenSetId,
      startScreen: CONSTANTS.gigyaConfig.registerScreenSet,
      lang: CONSTANTS.gigyaConfig.language,
      regSource: CONSTANTS.gigyaConfig.domain,
	  sessionExpiration: -2,
	  onAfterSubmit: this.registerEventHandler    
	});
  }

  /*
   * Gigya register event handler that is executed when user has submitted the register form
  */
 registerEventHandler = (eventObj) => {
	if (eventObj.response && eventObj.response.isRegistered) this.googleAnalyticsService.emitEvent('Mi_Perfil_SAM', 'Click', 'Registro_Completado', null);
  }
 
  /*
   * Returns a JSON Web Token (JWT) for the logged-in user
  */
  getJWT(): Observable <any> {
    return Observable.create((observer) => {
      gigya.accounts.getJWT({
        expiration: 7200, // 2 hours
        callback: retrieveResponse
      });
      function retrieveResponse(response) {
        observer.next(response.id_token);
        observer.complete();
      }
    });
  }

  /*
   * Returns if JSON Web Token (JWT) is expired
  */
  public isTokenExpired(): boolean {
    let isTokenExpired;
    const session = JSON.parse(this.localStorage.getItem('session'));
    session !== null && session.token ? isTokenExpired = this.jwtHelper.isTokenExpired(session.token) : isTokenExpired = true;
    return isTokenExpired;
  }

  /*
   * Retrieves subscription types from piano: if customer has in-app purchase subscriptions (iOS/Android), web subscriptions (Atabex), corporate subscriptions or no subscriptions
   * If customer does not have subscriptions, set type of user to Registered   
   * If customer has corporate subscription, set type of user to Corporate
   * If customer has web subscriptions, set type of user to Subscriber
   * If customer has in app subscriptions, set type of user to InAppSubscriber and calls method that shows in app subscriptions message
   * Then, set logged in state to true
  */
  getUserInfo() {
    this.subscriptionService.getPianoInfo(this.gigyaId, this.userProfile.email).subscribe((response) => {
      if(arrayInObjectIsEmpty(response, 'PianoUser_Device')) {
        this.typeOfUser = 'Registered';
      } else if (arrayContainsValue(response, 'PianoUser_Device', 'Corporate')) {
        this.typeOfUser = 'Corporate';
      } else if (arrayContainsValue(response, 'PianoUser_Device', 'Web')) {
        this.typeOfUser = 'Subscriber';
      } else if (arrayContainsValue(response, 'PianoUser_Device', 'iOS') || arrayContainsValue(response, 'PianoUser_Device', 'Android')) {
        this.typeOfUser = 'InAppSubscriber';
        this.subscriptionService.showInAppSubscriptionMessage(response['PianoUser_Device']);
      }
      this.setIsAuthenticated(true);
    });
  }

  /*  
   * Gigya logout function
  */
  logoutUserExpiredSession(): void {
    gigya.accounts.logout({
      callback: this.retrieveLogoutExpiredSession
    });
  }

  /*
   * Gigya logout event handler that is executed when session is expired
   * Removes session data and set logged in status to false
   * Does not redirect to homepage
  */
  retrieveLogoutExpiredSession = (response) => {
    if (response.status === CONSTANTS.status.okay && response.errorCode == 0) {
      this.token = null;
      this.localStorage.removeItem('session');
      this.userProfile = undefined;
      this.setIsAuthenticated(false);

      //remove localStorage.user-ref + sessionStorage.TermId  
      this.localStorage.removeItem('user-ref');
      this.sessionStorage.removeItem('TermId');
    } 
    else {
      console.error(response.errorMessage);
    }     
  }

  /*
   * Gigya logout function
  */
  public logout(): void {
    //remove localStorage.user-ref + sessionStorage.TermId  
    this.localStorage.removeItem('user-ref');
    this.sessionStorage.removeItem('TermId');

    gigya.accounts.logout({
      callback: this.retrieveLogoutResponse
    });
  }

  /*
   * Gigya logout event handler that is executed when user has successfully logged out
   * Removes session, set logged in status to false and redirects to homepage
  */
  retrieveLogoutResponse = (response) => {
    if (response.status === CONSTANTS.status.okay && response.errorCode == 0) {
      this.token = null;
      this.localStorage.removeItem('session');
      this.userProfile = undefined;
      this.setIsAuthenticated(false);
      this.router.navigate(['']).then(() => {
        window.location.reload();        
      });
    } 
    else {
      console.error(response.errorMessage);
    }     
  }

  /*
   * Gigya function to retrieve info of logged-in user (if the session is active)
  */
  public getAccountInfo(): Observable<any> {
    return Observable.create((observer) => {
      gigya.accounts.getAccountInfo({ 
        callback: getAccountInfoResponse 
      });
      function getAccountInfoResponse(eventObj) {
        let activeSession = eventObj.UID !== null && eventObj.UID !== '' && eventObj.errorCode === 0;
        if(activeSession) {
          observer.next(eventObj);
          observer.complete();
        }
    }
    });
  }

  /*
   * Show register Gigya screenset from activate subscription view
  */
  public registerOrLoginFromActivateSubscription(): void {
    gigya.accounts.showScreenSet({
      screenSet: 'suscripciones-login',
      startScreen: 'gigya-subscriber-register-screen',
      lang: CONSTANTS.gigyaConfig.language,
      regSource: CONSTANTS.gigyaConfig.domain,
      sessionExpiration: -2,
      containerID: 'registerContainer',
      onAfterScreenLoad: this.onAfterScreenLoadHandler
    });
    gigya.accounts.addEventHandlers({
      onLogin: this.loginFromActivateSubscriptionEventHandler
    });
  }

  /*
   * Autofill Gigya registration screenset after subscription is found (before activation)
  */
  onAfterScreenLoadHandler = () => {
    jQuery("input[name = 'email']").val(this.sharedDataService.userInfo.email);
    jQuery("input[name = 'profile.firstName']").val(this.sharedDataService.userInfo.name);
    jQuery("input[name = 'profile.lastName']").val(this.sharedDataService.userInfo.lastName);
  }

  /*
   * Gigya login event handler that is executed when user has successfully logged in after registering (from activate subscription view)
  */
  loginFromActivateSubscriptionEventHandler = (eventObj) => {
    console.log(eventObj);
    this.userProfile = eventObj.profile;
    this.gigyaId = eventObj.UID;
    this.getJWT().subscribe((response) => {
        this.token = response;
        this.localStorage.setItem('session', JSON.stringify({ token: response, uid: eventObj.UID, provider: eventObj.provider}));
        this.setIsAuthenticated(true);
    });
  }
  /*
   * Check if there is an active credit promotion to be offered with switch to easy pay
  */ 
  getCreditPromotion() {
    this.subscriptionService.getCreditPromotion();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src4156872633/src/src/app/authentication.service.ts